import { useCallback } from 'react'
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone'
import { ActivityIndicator } from 'react-native'

import { Color } from '../../style'

import { Button, ButtonProps } from '../button'
import { Icon } from '../icon'

import { UploadFileError } from './file'

export interface Props extends Omit<ButtonProps, 'name' | 'onPress'> {
  onSelect?: (value?: string, file?: File) => void
  onError?: (error: UploadFileError) => void
  options?: Omit<DropzoneOptions, 'onDrop' | 'disabled'>
  loading?: boolean
}

const UploadButton = (props: Props) => {
  const { disabled, light, onSelect, onError, options, loading, ...otherProps } = props
  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      onError?.(fileRejections[0]?.errors[0]?.code as UploadFileError)
      return
    }
    onSelect?.(URL.createObjectURL(acceptedFiles[0]), acceptedFiles[0])
  }, [])

  const { open, getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/bmp': [] },
    multiple: false,
    maxFiles: 1,
    maxSize: 20000000,
    minSize: 1000,
    disabled: !!disabled,
    noClick: true,
    ...options,
  })

  return (
    <div {...getRootProps()} data-testid="UploadButton.Dropzone">
      <Button
        left={
          loading ? (
            <ActivityIndicator size="small" color={Color.premiumBlack} />
          ) : (
            <Icon name="upload" color={light ? Color.premiumBlack : Color.simplyWhite} />
          )
        }
        disabled={disabled}
        light={light}
        onPress={open}
        testID="UploadButton.Button"
        {...otherProps}
      />
      <input {...getInputProps()} />
    </div>
  )
}

export default UploadButton
